import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import {
  ArrowLeft, Sparkle, Camera, Translate, CalendarCheck, ArrowRight,
  Receipt, Brain
} from '@phosphor-icons/react';

const AIPoweredPage = () => {
  const navigate = useNavigate();
  const { lang } = useWorkerLang();
  const L = (en, es) => (lang === 'es' ? es : en);

  const features = [
    {
      Icon: Camera,
      accent: '#D95800',
      title: L('AI Photo Estimator', 'Estimador de Fotos con IA'),
      desc: L('Snap up to 5 job-site photos and GPT-5.2 Vision returns a scoped 3-tier estimate (Economy / Standard / Premium) with line-item materials and labor — bid in minutes, not hours.',
              'Toma hasta 5 fotos del sitio y GPT-5.2 Vision devuelve un estimado de 3 niveles (Económico / Estándar / Premium) con materiales y mano de obra detallados — presupuesta en minutos, no en horas.'),
      route: '/ai-pricing',
      cta: L('Try the Estimator', 'Probar el Estimador'),
    },
    {
      Icon: Translate,
      accent: '#10B981',
      title: L('12-Language Voice Translator', 'Traductor de Voz en 12 Idiomas'),
      desc: L('Tap, speak, and hear an instant translation across 12 languages. Powered by Whisper + GPT-5.2 + OpenAI TTS, it keeps construction terms accurate (rebar ↔ varilla, drywall ↔ tablaroca).',
              'Toca, habla y escucha una traducción instantánea en 12 idiomas. Con Whisper + GPT-5.2 + OpenAI TTS, mantiene precisos los términos de construcción (rebar ↔ varilla, drywall ↔ tablaroca).'),
      route: '/translator',
      cta: L('Open the Translator', 'Abrir el Traductor'),
    },
    {
      Icon: CalendarCheck,
      accent: '#2B4C6F',
      title: L('AI Scheduling Engine', 'Motor de Programación con IA'),
      desc: L('Describe the job and the AI builds a realistic crew schedule — sequencing tasks, spotting conflicts, and keeping every milestone on track automatically.',
              'Describe el trabajo y la IA arma un cronograma realista para la cuadrilla — ordena tareas, detecta conflictos y mantiene cada etapa en marcha automáticamente.'),
      route: '/how-it-works',
      cta: L('See How It Works', 'Ver Cómo Funciona'),
    },
  ];

  const extras = [
    { Icon: Receipt, title: L('AI Shopping Lists & Receipt OCR', 'Listas de Compras con IA y OCR de Recibos') },
    { Icon: Brain, title: L('Smart Pricing Suggestions', 'Sugerencias de Precios Inteligentes') },
    { Icon: Sparkle, title: L('AI-Personalized Outreach', 'Difusión Personalizada con IA') },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-[#0A0F1A]" data-testid="ai-powered-page">
      {/* Header */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-white hover:text-[#D95800]" data-testid="ai-back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="inline-flex items-center gap-2 bg-[#D95800] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-black">
            <Sparkle size={14} weight="fill" /> {L('Built With The Best AI', 'Creado Con La Mejor IA')}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-[#2B4C6F] border-b-4 border-black">
        <div className="max-w-5xl mx-auto px-4 py-14 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
            {L('How AI Powers ConstructHelp', 'Cómo la IA Impulsa ConstructHelp')}
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            {L('We built ConstructHelp with the best AI models so contractors, workers, and homeowners get real tools that save time on every job — not gimmicks.',
               'Creamos ConstructHelp con los mejores modelos de IA para que contratistas, trabajadores y propietarios tengan herramientas reales que ahorran tiempo en cada trabajo — sin trucos.')}
          </p>
        </div>
      </div>

      {/* Feature cards */}
      <div className="max-w-5xl mx-auto px-4 py-14 space-y-6">
        {features.map((f, i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#0F172A] border-2 border-black p-6 md:p-8 shadow-[6px_6px_0px_#000] flex flex-col md:flex-row md:items-center gap-6"
            data-testid={`ai-feature-${i}`}
          >
            <div
              className="w-16 h-16 rounded-xl border-2 border-black flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: f.accent }}
            >
              <f.Icon size={32} weight="fill" className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight text-neutral-900 dark:text-white mb-2">
                {f.title}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-300 text-sm md:text-base leading-relaxed">
                {f.desc}
              </p>
            </div>
            <Button
              onClick={() => navigate(f.route)}
              className="h-12 px-6 bg-neutral-900 hover:bg-black text-white font-bold uppercase border-2 border-black shadow-[3px_3px_0px_#D95800] rounded-none flex-shrink-0"
              data-testid={`ai-feature-cta-${i}`}
            >
              {f.cta}
              <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </div>
        ))}

        {/* Extras */}
        <div className="grid sm:grid-cols-3 gap-4 pt-4">
          {extras.map((e, i) => (
            <div key={i} className="bg-white dark:bg-[#0F172A] border-2 border-black p-5 shadow-[3px_3px_0px_#000] flex items-center gap-3">
              <e.Icon size={24} weight="fill" className="text-[#D95800] flex-shrink-0" />
              <span className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{e.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#0F172A] border-t-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-4">
            {L('Put AI to Work on Your Next Job', 'Pon la IA a Trabajar en tu Próximo Proyecto')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/signup')}
              className="h-12 px-8 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[4px_4px_0px_#000] rounded-none"
              data-testid="ai-signup-cta"
            >
              {L('Get Started Free', 'Empieza Gratis')}
            </Button>
            <Button
              onClick={() => navigate('/how-it-works')}
              variant="outline"
              className="h-12 px-8 bg-transparent text-white font-bold uppercase border-2 border-white rounded-none hover:bg-white/10"
            >
              {L('How It Works', 'Cómo Funciona')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPoweredPage;
